import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { IconButton, Popover } from '@scaleaq/scaleui';
import { Bell } from '@scaleaq/sui-icons/react/icons';

import { Z_INDEX } from '@/constants';
import { colors } from '@/utils';

import {
  StyledAlertBellAnchor,
  StyledAlertCount,
  StyledAlertMenuItem,
  StyledAlertMenuList,
} from './cameraStreamAlertMenu/cameraStreamAlertMenu.styles.ts';

/** Short delay so a pointer just passing over the bell does not flash the menu. */
const OPEN_DELAY_MS = 100;
/** Long enough to move the pointer from the bell across the gap into the menu. */
const CLOSE_DELAY_MS = 300;

export type CameraStreamAlert = {
  key: string;
  content: ReactNode;
};

type CameraStreamAlertMenuProps = {
  alerts: CameraStreamAlert[];
  /** Accessible name of the menu. */
  label: string;
  /** Accessible name of the bell; include the alert count, which is only shown visually. */
  bellLabel: string;
  /** Fill colour of the bell; use a palette colour so it reads the same in both themes. */
  color: string;
  /** False in fullscreen: the menu then renders into the fullscreen element, above its overlays. */
  usePortal: boolean;
};

/**
 * Bell filled with the alert colour and showing the alert count; it opens a list of alerts.
 * Opens on hover or click and closes when the pointer leaves the bell and the menu, or on a press
 * outside them; a touch tap on the bell toggles it. Opened from the keyboard (Enter/Space/
 * ArrowDown), it moves focus into the menu and stays open until focus leaves it or Escape is
 * pressed.
 */
export const CameraStreamAlertMenu = ({
  alerts,
  label,
  bellLabel,
  color,
  usePortal,
}: CameraStreamAlertMenuProps) => {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  /** Set while the menu was opened from the keyboard: pointer leave must not close it then. */
  const keyboardOpenRef = useRef(false);
  /** Pointer type of the last press on the bell; touch has no hover, so a tap toggles. */
  const bellPointerTypeRef = useRef('mouse');
  /** Set by a press on the bell or the menu, so the focus move it causes does not close it. */
  const pressedInsideRef = useRef(false);
  const pressCountRef = useRef(0);

  const clearTimer = useCallback(() => clearTimeout(timerRef.current), []);
  useEffect(() => clearTimer, [clearTimer]);

  const scheduleOpen = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [clearTimer]);

  const scheduleClose = useCallback(() => {
    clearTimer();
    if (keyboardOpenRef.current) return;
    timerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    keyboardOpenRef.current = false;
    setOpen(false);
  }, [clearTimer]);

  const getItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  // Keyboard opening moves focus to the first item once the menu has mounted.
  useEffect(() => {
    if (open && keyboardOpenRef.current) getItems()[0]?.focus();
  }, [open]);

  // A press anywhere outside the bell and the menu closes it (touch has no pointer leave).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (ev: globalThis.PointerEvent) => {
      const target = ev.target as Node;
      if (!menuRef.current?.contains(target) && !bellRef.current?.contains(target)) close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, close]);

  // If the bell disappears (the last alert cleared) while focus is on it or in the menu, hand
  // focus to the nearest focusable ancestor (the camera widget) instead of dropping it to <body>.
  useLayoutEffect(() => {
    const bell = bellRef.current;
    return () => {
      const active = document.activeElement;
      const menu = document.getElementById(menuId);
      if (!bell || !(bell.contains(active) || menu?.contains(active))) return;
      bell.parentElement?.closest<HTMLElement>('[tabindex="0"]')?.focus({ preventScroll: true });
    };
  }, [menuId]);

  const openWithKeyboard = () => {
    clearTimer();
    keyboardOpenRef.current = true;
    if (open) getItems()[0]?.focus();
    else setOpen(true);
  };

  const handleBellClick = (ev: MouseEvent<HTMLButtonElement>) => {
    // Enter/Space on a button fire a click with `detail` 0; a real pointer click counts presses.
    if (ev.detail === 0) {
      openWithKeyboard();
      return;
    }
    clearTimer();
    if (open && bellPointerTypeRef.current !== 'mouse') {
      close();
      return;
    }
    keyboardOpenRef.current = false;
    setOpen(true);
  };

  const handleBellKeyDown = (ev: KeyboardEvent<HTMLButtonElement>) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      openWithKeyboard();
    }
    if (ev.key === 'Escape' && open) close();
  };

  // Cleared once the press's own events are done, after any focus-leave check it queued. Only the
  // latest press clears it, so an earlier press's reset can't cancel a newer one.
  const markPressedInside = () => {
    const press = ++pressCountRef.current;
    pressedInsideRef.current = true;
    setTimeout(() => {
      if (pressCountRef.current === press) pressedInsideRef.current = false;
    });
  };

  const handleBellPointerDown = (ev: PointerEvent<HTMLButtonElement>) => {
    bellPointerTypeRef.current = ev.pointerType;
    markPressedInside();
  };

  // Using the mouse in a keyboard-opened menu hands it back to pointer behaviour.
  const handleMenuPointerDown = () => {
    keyboardOpenRef.current = false;
    markPressedInside();
  };

  const handleMenuKeyDown = (ev: KeyboardEvent<HTMLUListElement>) => {
    const items = getItems();
    const index = items.indexOf(document.activeElement as HTMLElement);
    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        items[(index + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        ev.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        ev.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        ev.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Escape':
        bellRef.current?.focus();
        close();
        break;
      case 'Tab':
        // Hand focus back to the bell; the default Tab then moves on from there.
        bellRef.current?.focus();
        close();
        break;
    }
  };

  const handleFocusLeave = () => {
    // Checked after the current pointer event. The camera widget focuses itself on every
    // pointer-down, so a press on the bell or the menu can move focus out (a touch tap often never
    // focuses the item); that press is ignored. Presses outside are closed by the pointer-down
    // listener above, so this only has to catch focus leaving by keyboard.
    setTimeout(() => {
      if (pressedInsideRef.current) return;
      const active = document.activeElement;
      if (!menuRef.current?.contains(active) && !bellRef.current?.contains(active)) close();
    });
  };

  // Keep the widget from starting a grid drag or toggling fullscreen (double-click). React
  // events from the portaled menu still bubble to the widget, so the menu needs this too.
  const stopPropagation = (ev: SyntheticEvent) => ev.stopPropagation();

  const popover = (
    <Popover
      anchorEl={bellRef as RefObject<HTMLElement>}
      open={open}
      onClose={close}
      placement='bottom-end'
      zIndex={Z_INDEX.content.floating}
      // Rendered in place: the createPortal below already picks the body or fullscreen element.
      usePortal={false}
      disableBackdrop
      // Popover's own Escape listener is window-wide; the bell and the menu handle Escape.
      disableCloseOnEscape
      onPointerDown={handleMenuPointerDown}
      onMouseEnter={clearTimer}
      onMouseLeave={scheduleClose}
      onMouseDown={stopPropagation}
      onDoubleClick={stopPropagation}
      popoverElement={
        <StyledAlertMenuList
          ref={menuRef}
          id={menuId}
          role='menu'
          aria-label={label}
          onKeyDown={handleMenuKeyDown}
          onBlur={handleFocusLeave}
        >
          {alerts.map(({ key, content }) => (
            <StyledAlertMenuItem key={key} role='menuitem' tabIndex={-1}>
              {content}
            </StyledAlertMenuItem>
          ))}
        </StyledAlertMenuList>
      }
    />
  );

  return (
    <StyledAlertBellAnchor>
      <IconButton
        ref={bellRef}
        size='large'
        color='neutral'
        // Palette (not theme) colours: the bell sits on video in both light and dark mode.
        // `color` goes in `style`: the global `svg { color }` in reset.css beats the SVG attribute.
        Icon={<Bell size='large' style={{ color: colors.palette.white[1000] }} fill={color} />}
        aria-label={bellLabel}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={handleBellClick}
        onKeyDown={handleBellKeyDown}
        onBlur={handleFocusLeave}
        onPointerDown={handleBellPointerDown}
        onMouseDown={stopPropagation}
        onDoubleClick={stopPropagation}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
      />
      <StyledAlertCount type='body_medium' aria-hidden>
        {alerts.length}
      </StyledAlertCount>
      {createPortal(
        popover,
        usePortal ? document.body : (document.fullscreenElement ?? document.body)
      )}
    </StyledAlertBellAnchor>
  );
};
